/**
 * Max distance (pointer → joint, image px) for hover and starting a pose drag.
 * Modest radius keeps handles from feeling huge; pair with a high snap fraction for “near” picks.
 */
export type PoseLandmarkRadiusOptions = {
  /** Larger touch targets and slightly looser snap on narrow viewports. */
  mobile?: boolean;
};

export function getPoseLandmarkAcquireRadiusPx(
  canvasWidthCssPx: number,
  options?: PoseLandmarkRadiusOptions
): number {
  if (options?.mobile) {
    return Math.max(44, canvasWidthCssPx * 0.028);
  }
  return Math.max(30, canvasWidthCssPx * 0.02);
}

/** Larger radius for hover highlight only — pick / pointer-down stays on acquire radius. */
export function getPoseLandmarkHoverMagnetRadiusPx(
  canvasWidthCssPx: number,
  options?: PoseLandmarkRadiusOptions
): number {
  if (options?.mobile) {
    return Math.max(56, canvasWidthCssPx * 0.04);
  }
  return Math.max(48, canvasWidthCssPx * 0.034);
}

/** Image-space hit radius for ASIS/PSIS (and AP pelvis) pins — aligned with pose targets on mobile. */
export function getPelvisPinAcquireRadiusPx(
  canvasBitmapWidth: number,
  options?: PoseLandmarkRadiusOptions
): number {
  if (options?.mobile) {
    return Math.max(46, canvasBitmapWidth * 0.03);
  }
  return Math.max(24, canvasBitmapWidth * 0.022);
}

/** Larger hover-only radius so pelvis pins feel selectable like pose points. */
export function getPelvisPinHoverMagnetRadiusPx(
  canvasBitmapWidth: number,
  options?: PoseLandmarkRadiusOptions
): number {
  if (options?.mobile) {
    return Math.max(56, canvasBitmapWidth * 0.038);
  }
  return Math.max(36, canvasBitmapWidth * 0.03);
}

/**
 * Half-width (image px) for treating a pointer as “on” the pelvis red line for grabs.
 * Wider on mobile so the line is draggable without hitting the endpoints.
 */
export function getPelvisRedLineGrabHalfWidthPx(
  canvasBitmapWidth: number,
  options?: PoseLandmarkRadiusOptions
): number {
  if (options?.mobile) {
    return Math.max(34, canvasBitmapWidth * 0.024);
  }
  return Math.max(18, canvasBitmapWidth * 0.014);
}

/**
 * Inside `acquireRadius *` this fraction, grab offset is zero (pointer behaves as if on the joint).
 * High value ⇒ “mouse near” still snaps drag to the landmark center.
 */
export const POSE_LANDMARK_SNAP_TO_CENTER_FRAC = 0.88;

/** Slightly lower on mobile so edge touches don’t feel like the joint jumps. */
export const POSE_LANDMARK_SNAP_TO_CENTER_FRAC_MOBILE = 0.72;

/** Client-pixel movement before a “tap on empty” becomes a drag (mobile two-phase). */
export const MOBILE_POSE_DRAG_SLOP_PX = 8;

/**
 * Grab offset (image space) from pointer to landmark at pointerdown.
 * When the pointer lies inside the snap disk, offset is (0,0).
 */
export function computePoseDragGrabOffset(
  imgPt: { x: number; y: number },
  lm: { x: number; y: number },
  acquireRadius: number,
  snapToCenterFrac: number = POSE_LANDMARK_SNAP_TO_CENTER_FRAC
): { x: number; y: number } {
  let ox = imgPt.x - lm.x;
  let oy = imgPt.y - lm.y;
  const innerR = acquireRadius * snapToCenterFrac;
  if (ox * ox + oy * oy <= innerR * innerR) {
    return { x: 0, y: 0 };
  }
  return { x: ox, y: oy };
}
